import styled from '@emotion/styled'
import start from './unsafe-startup'
import CBOR from 'cbor-js'
import * as React from 'react'
import * as R from 'ramda'
import ReactDOM from 'react-dom'
import {
  Center,
  ChakraProvider,
  extendTheme,
  Flex,
  Box,
  VStack,
  Heading,
  Button,
  Text,
  Image,
  Input,
  Select,
  Link,
} from '@chakra-ui/react'

import type { ThemeConfig } from '@chakra-ui/react'

import type { GameState, PlayerState } from './types'
import type {
  ClientAuthMessage,
  ServerMessage,
  SocketMessage,
  CommandMessage,
  PacketMessage,
} from './protocol'
import { GameStateType, WeaponType } from './types'
import { MessageType, ENetEventType } from './protocol'
import StatusOverlay from './Loading'
import NAMES from './names'
import useAssets, { getInstalledMods, mountFile } from './assets/hook'
import useAuth, {
  DISCORD_CODE,
  renderDiscordHeader,
  renderDiscordButton,
} from './discord'
import { CubeMessageType } from './game'
import * as cube from './game'
import MobileControls from './MobileControls'
import FileDropper from './FileDropper'
import FRAGFI_LOGO from 'url:./brand/logo.jpg'
import FRAGFI_BANNER from 'url:./brand/banner.jpg'

import type { PromiseSet } from './utils'
import { CONFIG } from './config'
import { breakPromise, BROWSER } from './utils'
import * as log from './logging'

import { LoadRequestType } from './assets/types'

start()

const colors = {
  brand: {
    900: '#1a365d',
    800: '#153e75',
    700: '#2a69ac',
  },
}

const config: ThemeConfig = {
  initialColorMode: 'dark',
  useSystemColorMode: false,
}

const theme = extendTheme({ colors, config })

const OuterContainer = styled.div`
  touch-action: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`

const GameContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 0;
`

const LoadingContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 1;
`

const DropTarget = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  pointer-events: none;
  background: var(--chakra-colors-yellow-500);
  z-index: 2;
`

const StartPageContainer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 4;
  pointer-events: auto;
  overflow-y: auto;
  background:
    radial-gradient(circle at 50% 18%, rgba(224, 36, 36, 0.28), transparent 34%),
    linear-gradient(180deg, rgba(5, 7, 13, 0.97), rgba(5, 7, 13, 0.91) 48%, rgba(2, 4, 8, 0.99));
  color: white;
`

const PLAYER_SKINS = [
  { id: 0, label: '1' },
  { id: 1, label: '2' },
  { id: 2, label: '3' },
  { id: 3, label: '4' },
  { id: 4, label: '5' },
]

const cleanPlayerName = (name: string, fallback = 'Karat FPS') => {
  const cleaned = name.replace(/[^a-zA-Z0-9_\-]/g, '').slice(0, 15)
  return cleaned.length > 0 ? cleaned : fallback
}

type EarningsLeader = {
  username: string
  kills_total: number
  gold_earned: number
}

type StartPageProps = {
  walletStatus: string
  walletAddress: string
  username: string
  selectedSkin: number
  earningsLeaderboard: EarningsLeader[]
  onUsernameChange: (value: string) => void
  onWalletChange: (value: string) => void
  onSkinChange: (value: number) => void
  onJoinGame: () => void
  onGuestStart: () => void
}

function StartPage(props: StartPageProps) {
  const {
    walletStatus,
    walletAddress,
    username,
    selectedSkin,
    earningsLeaderboard,
    onUsernameChange,
    onWalletChange,
    onSkinChange,
    onJoinGame,
    onGuestStart,
  } = props
  return (
    <StartPageContainer>
      <Flex align="center" justify="center" minH="100%" px={{ base: 4, md: 6 }} py={{ base: 4, md: 5 }}>
        <VStack
          spacing={3}
          maxW="720px"
          w="100%"
          p={{ base: 4, md: 5 }}
          border="1px solid rgba(255,255,255,0.16)"
          borderRadius="28px"
          bg="rgba(5, 7, 13, 0.84)"
          boxShadow="0 30px 90px rgba(0,0,0,0.58), 0 0 70px rgba(224,36,36,0.22)"
          backdropFilter="blur(14px)"
        >
          <Image
            src={FRAGFI_BANNER}
            alt="Karat FPS"
            maxW="430px"
            maxH="118px"
            objectFit="contain"
            w="100%"
            borderRadius="20px"
            border="1px solid rgba(255,255,255,0.12)"
          />
          <VStack spacing={2} textAlign="center">
            <Image src={FRAGFI_LOGO} alt="Karat FPS logo" boxSize="56px" borderRadius="16px" />
            <Heading size="xl" letterSpacing="0.08em" textTransform="uppercase">
              Karat FPS Main Menu
            </Heading>
            <Text color="whiteAlpha.800" fontSize={{ base: 'md', md: 'lg' }}>
              Choose your username. Wallet is optional for GLD rewards.
            </Text>
          </VStack>
          <VStack spacing={3} w="100%" maxW="460px">
            <Input
              value={username}
              onChange={(event) => onUsernameChange(event.target.value)}
              placeholder="Username"
              maxLength={15}
              bg="rgba(255,255,255,0.08)"
              borderColor="whiteAlpha.400"
              _placeholder={{ color: 'whiteAlpha.500' }}
            />
            <Text w="100%" color="whiteAlpha.900" fontSize="sm" fontWeight="700" textAlign="left">
              Wallet (optional rewards)
            </Text>
            <Input
              value={walletAddress}
              onChange={(event) => onWalletChange(event.target.value)}
              placeholder="0x..."
              maxLength={64}
              autoCapitalize="none"
              autoCorrect="off"
              bg="rgba(255,255,255,0.08)"
              borderColor="whiteAlpha.400"
              _placeholder={{ color: 'whiteAlpha.500' }}
            />
            <Select
              value={selectedSkin}
              onChange={(event) => onSkinChange(Number(event.target.value))}
              bg="rgba(255,255,255,0.08)"
              borderColor="whiteAlpha.400"
            >
              {PLAYER_SKINS.map((skin) => (
                <option key={skin.id} value={skin.id}>
                  {skin.label}
                </option>
              ))}
            </Select>
            <Button
              size="lg"
              w="100%"
              bg="#e02424"
              color="white"
              _hover={{ bg: '#ff3333' }}
              _active={{ bg: '#b91c1c' }}
              onClick={onJoinGame}
            >
              Play
            </Button>
            <Button
              size="lg"
              w="100%"
              variant="outline"
              borderColor="whiteAlpha.500"
              color="white"
              _hover={{ bg: 'whiteAlpha.200' }}
              onClick={onGuestStart}
            >
              Spectate / Fly Around
            </Button>
          </VStack>
          <Box w="100%" maxW="460px" border="1px solid rgba(255,255,255,0.12)" borderRadius="18px" p={3} bg="rgba(255,255,255,0.045)">
            <Text color="whiteAlpha.900" fontSize="sm" fontWeight="800" letterSpacing="0.08em" textTransform="uppercase" mb={2}>
              All-time GLD Earned
            </Text>
            {earningsLeaderboard.length > 0 ? (
              <VStack spacing={1} align="stretch">
                {earningsLeaderboard.slice(0, 5).map((player, index) => (
                  <Flex key={`${player.username}-${index}`} justify="space-between" gap={3} color="whiteAlpha.850" fontSize="sm">
                    <Text noOfLines={1}>{index + 1}. {player.username}</Text>
                    <Text flexShrink={0}>{Number(player.gold_earned).toFixed(5)} GLD</Text>
                  </Flex>
                ))}
              </VStack>
            ) : (
              <Text color="whiteAlpha.600" fontSize="sm">Loading leaderboard...</Text>
            )}
          </Box>
          <Text color="whiteAlpha.700" fontSize="sm" textAlign="center">
            {walletStatus}
          </Text>
          <Link
            href="https://karatfps.pages.dev"
            isExternal
            color="whiteAlpha.800"
            fontSize="sm"
            fontWeight="700"
            letterSpacing="0.04em"
            textTransform="uppercase"
            _hover={{ color: '#ff3333', textDecoration: 'none' }}
          >
            Follow us on Twitter
          </Link>
        </VStack>
      </Flex>
    </StartPageContainer>
  )
}

const pushURLState = (url: string) => {
  const {
    location: { search: params },
  } = window

  // We only want to keep 'mods', everything else goes
  const parsedParams = new URLSearchParams(params)
  const newParams = new URLSearchParams()
  if (parsedParams.has('mods')) {
    const value = parsedParams.get('mods')
    if (value != null) {
      newParams.set('mods', value)
    }
  }

  window.history.pushState({}, '', `${url}${newParams.toString()}`)
}

const clearURLState = () => pushURLState('/')

export type CommandRequest = {
  id: number
  promiseSet: PromiseSet<string>
}

const DEBUG = false
const DELAY_AFTER_LOAD: CubeMessageType[] = [
  CubeMessageType.N_ITEMLIST,
  CubeMessageType.N_SPAWN,
]

const SERVER_URL_REGEX = /#\/server\/([\w.]+)\/?(\d+)?/
const MAP_URL_REGEX = /#\/map\/(\w+)/
const DEMO_URL_REGEX = /#\/demo\/(\w+)/

let loadedMods: string[] = []
let failedMods: string[] = []
const KARAT_API_URL = 'https://api.karatfps.lol'
const SAVED_WALLET_KEY = 'karat_wallet_address'
const WALLET_COOKIE_NAME = 'karat_wallet'
const EVM_WALLET_REGEX = /^0x[a-fA-F0-9]{40}$/

const readSavedWallet = () => {
  try {
    const stored = window.localStorage?.getItem(SAVED_WALLET_KEY)?.trim() ?? ''
    if (EVM_WALLET_REGEX.test(stored)) return stored
    const cookie = document.cookie
      .split('; ')
      .find((entry) => entry.startsWith(`${WALLET_COOKIE_NAME}=`))
      ?.slice(WALLET_COOKIE_NAME.length + 1)
    const decoded = cookie != null ? decodeURIComponent(cookie).trim() : ''
    return EVM_WALLET_REGEX.test(decoded) ? decoded : ''
  } catch {
    return ''
  }
}

const saveWallet = (wallet: string) => {
  try {
    if (!EVM_WALLET_REGEX.test(wallet)) return
    window.localStorage?.setItem(SAVED_WALLET_KEY, wallet)
    document.cookie = `${WALLET_COOKIE_NAME}=${encodeURIComponent(wallet)}; Max-Age=31536000; Path=/; SameSite=Lax; Secure`
  } catch {
    // Best-effort browser persistence only.
  }
}


async function playDemoURL(url: string, reference: string) {
  log.info(`loading demo ${reference}`)
  try {
    const response = await fetch(url)
    if (response.status === 404) {
      log.error(`demo ${reference} not found`)
      return
    }
    if (response.status !== 200) {
      throw Error('failed to fetch')
    }

    const data = await response.arrayBuffer()
    const demoName = `/demo/${reference}.dmo`
    await mountFile(demoName, new Uint8Array(data))
    BananaBread.execute(`demo ${reference}.dmo`)
  } catch (e) {
    log.error('failed to play demo')
  }
}

function App() {
  const [state, setState] = React.useState<GameState>({
    type: GameStateType.PageLoading,
  })
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = React.useState<{
    width: Maybe<number>
    height: Maybe<number>
  }>({ width: null, height: null })

  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const wsRef = React.useRef<WebSocket>()
  const wsQueue = React.useRef<ArrayBuffer[]>([])
  const [walletAddress, setWalletAddress] = React.useState<string>(() => readSavedWallet())
  const [walletProof, setWalletProof] = React.useState<Maybe<{
    walletType: 'solana' | 'evm'
    message: string
    signature: string
    signatureEncoding: 'base64' | 'hex'
  }>>(null)
  const walletAuthRef = React.useRef({ address: '', loggedIn: false })
  const solanaWalletRef = React.useRef<any>(null)
  const [walletStatus, setWalletStatus] = React.useState<string>(() => {
    const saved = readSavedWallet()
    return saved.length > 0 ? `Wallet saved: ${saved.slice(0, 6)}...${saved.slice(-4)}` : 'Wallet optional. Add one only if you want GLD rewards.'
  })
  const [claimStatus, setClaimStatus] = React.useState<string>('No GLD payout pending')
  const [earningsLeaderboard, setEarningsLeaderboard] = React.useState<EarningsLeader[]>([])
  const [chosenUsername, setChosenUsername] = React.useState<string>('')
  const [privateLink, setPrivateLink] = React.useState<string>('')
  const [privateStatus, setPrivateStatus] = React.useState<string>('Create a private link to invite friends directly.')

  const send = React.useCallback((data: ArrayBuffer) => {
    const { current: ws } = wsRef
    const { current: queue } = wsQueue
    if (ws == null || ws.readyState !== WebSocket.OPEN) {
      wsQueue.current = [...queue, data]
      return
    }
    ws.send(data)
  }, [])

  const sendAuthMessage = React.useCallback((message: ClientAuthMessage) => {
    send(CBOR.encode(message))
  }, [])

  const {
    loadAsset,
    loadAssetProgress,
    getMod,
    onReady: onReadyAssets,
  } = useAssets(setState)
  const {
    state: authState,
    receiveMessage: receiveAuthMessage,
    initialize: initializeDiscord,
  } = useAuth(sendAuthMessage)

  const [playerState, setPlayerState] = React.useState<PlayerState>({
    health: 0,
    maxHealth: 0,
    weapon: WeaponType.Pistol,
    ammo: {
      [WeaponType.Saw]: 0,
      [WeaponType.Shotgun]: 0,
      [WeaponType.Chaingun]: 0,
      [WeaponType.Rocket]: 0,
      [WeaponType.Rifle]: 0,
      [WeaponType.Grenade]: 0,
      [WeaponType.Pistol]: 0,
    },
  })

  const refreshMainMenu = React.useCallback(() => {
    // Avoid flicker: the React effect below re-injects the menu text when state changes.
    // Do not force-close/reopen the Cube menu on every wallet/join click.
  }, [])

  React.useEffect(() => {
    walletAuthRef.current = {
      address: walletAddress,
      loggedIn: walletAddress.length > 0,
    }
    saveWallet(walletAddress)
  }, [walletAddress, walletProof])

  React.useEffect(() => {
    const handlePaste: EventListener = (event) => {
      const pasted = (event as ClipboardEvent).clipboardData?.getData('text')?.trim() ?? ''
      const match = pasted.match(/0x[a-fA-F0-9]{40}/)
      if (!match) return

      const wallet = match[0]
      event.preventDefault()
      setWalletAddress(wallet)
      setWalletProof(null)
      setWalletStatus(`Wallet pasted: ${wallet.slice(0, 6)}...${wallet.slice(-4)}`)
    }

    window.addEventListener('paste', handlePaste, true)
    return () => window.removeEventListener('paste', handlePaste, true)
  }, [])

  const sanitizeUsername = React.useCallback((username: string): string => {
    return username.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 16)
  }, [])

  const isChosenUsername = React.useCallback((username: string): boolean => {
    const cleaned = sanitizeUsername(username)
    return cleaned.length >= 3 && cleaned.toLowerCase() !== 'unnamed'
  }, [sanitizeUsername])

  const bytesToBase64 = React.useCallback((bytes: Uint8Array): string => {
    let binary = ''
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte)
    })
    return window.btoa(binary)
  }, [])

  const buildWalletMessage = React.useCallback((wallet: string): string => {
    return [
      'Karat FPS wallet login',
      `Wallet: ${wallet}`,
      `Host: ${window.location.host}`,
      `Timestamp: ${Date.now()}`,
    ].join('\n')
  }, [])

  const buildClaimMessage = React.useCallback((wallet: string): string => {
    return [
      'Karat FPS token claim',
      `Wallet: ${wallet}`,
      `Host: ${window.location.host}`,
      `Timestamp: ${Date.now()}`,
    ].join('\n')
  }, [])

  const readApiJson = React.useCallback(async (response: Response): Promise<any> => {
    const text = await response.text()
    try {
      return text.length > 0 ? JSON.parse(text) : {}
    } catch {
      throw new Error(text.slice(0, 120) || `API returned ${response.status}`)
    }
  }, [])

  const refreshEarningsLeaderboard = React.useCallback(async () => {
    const response = await fetch(`${KARAT_API_URL}/earnings-leaderboard?limit=5`)
    const result = await readApiJson(response)
    if (!response.ok || result?.ok === false) {
      throw new Error(result?.error || 'leaderboard lookup failed')
    }
    const rows = Array.isArray(result?.leaderboard) ? result.leaderboard : []
    setEarningsLeaderboard(rows.map((row: any) => ({
      username: String(row.username ?? 'player'),
      kills_total: Number(row.kills_total ?? 0),
      gold_earned: Number(row.gold_earned ?? 0),
    })))
    return result
  }, [readApiJson])

  React.useEffect(() => {
    refreshEarningsLeaderboard().catch(() => null)
    const interval = window.setInterval(() => {
      refreshEarningsLeaderboard().catch(() => null)
    }, 30000)
    return () => window.clearInterval(interval)
  }, [refreshEarningsLeaderboard])

  const registerRewardUser = React.useCallback(async (username: string, wallet: string) => {
    const response = await fetch(`${KARAT_API_URL}/users`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ username, wallet }),
    })
    const result = await readApiJson(response)
    if (!response.ok || result?.ok === false) {
      throw new Error(result?.error || 'reward registration failed')
    }
    return result
  }, [readApiJson])

  const refreshClaimBalance = React.useCallback(async (wallet: string) => {
    const response = await fetch(`${KARAT_API_URL}/claims?wallet=${encodeURIComponent(wallet)}`)
    const result = await readApiJson(response)
    if (!response.ok || result?.ok === false) {
      throw new Error(result?.error || 'reward lookup failed')
    }
    const pendingKills = Number(result?.pending?.kills ?? 0)
    const pendingTokens = Number(result?.pending?.tokens ?? 0)
    setClaimStatus(pendingKills > 0 ? `${pendingKills} karats = ${pendingTokens} GLD pending payout.` : 'No GLD payout pending.')
    return result
  }, [readApiJson])


  const connectToLobby = React.useCallback(
    (spectatorOnly: boolean, wallet = '', username = '') => {
      send(
        CBOR.encode({
          Op: MessageType.WalletLogin,
          Wallet: wallet,
          WalletType: !spectatorOnly && wallet.length > 0 ? 'typed-evm' : undefined,
          Message: spectatorOnly ? undefined : walletProof?.message,
          Signature: spectatorOnly ? undefined : walletProof?.signature,
          SignatureEncoding: spectatorOnly ? undefined : walletProof?.signatureEncoding,
          SpectatorOnly: spectatorOnly,
        })
      )
      send(
        CBOR.encode({
          Op: MessageType.Connect,
          Target: 'lobby',
        })
      )
      if (spectatorOnly) {
        const specName = `SPEC${Math.floor(Math.random() * 9999)}`
        BananaBread.execute(`name ${specName}`)
        setWalletStatus('Spectating. Wallet optional for rewards.')
        return
      }
      if (username.length > 0) {
        ;[0, 250, 750, 1500].forEach((delay) => {
          setTimeout(() => {
            BananaBread.execute(`name ${username}`)
            BananaBread.execute('spectator 0')
          }, delay)
        })
      }
      setWalletStatus(wallet.length > 0 ? `Wallet: ${wallet.slice(0, 6)}...${wallet.slice(-4)}` : 'Playing without rewards wallet.')
    },
    [send, walletProof]
  )

  const startAsGuest = React.useCallback(() => {
    connectToLobby(true, '')
  }, [connectToLobby])

  const joinGame = React.useCallback((username = chosenUsername) => {
    const cleanedUsername = sanitizeUsername(username)
    const cleanedWallet = walletAddress.trim()
    const wantsRewards = cleanedWallet.length > 0
    if (wantsRewards && !/^0x[a-fA-F0-9]{40}$/.test(cleanedWallet)) {
      setWalletStatus('Wallet is optional, but if entered it must be a valid 0x address.')
      refreshMainMenu()
      return
    }
    if (!isChosenUsername(cleanedUsername)) {
      setWalletStatus('Choose a name, 3-16 letters/numbers/underscore.')
      refreshMainMenu()
      return
    }

    setChosenUsername(cleanedUsername)
    BananaBread.execute(`name ${cleanedUsername}`)
    connectToLobby(false, cleanedWallet, cleanedUsername)
    setWalletStatus(`Joining as ${cleanedUsername}...`)
    refreshMainMenu()

    ;(async () => {
      try {
        if (!wantsRewards) {
          setWalletStatus('Playing without rewards wallet.')
          refreshMainMenu()
          return
        }
        await registerRewardUser(cleanedUsername, cleanedWallet)
        await refreshClaimBalance(cleanedWallet).catch(() => null)
        setWalletStatus(`Wallet: ${cleanedWallet.slice(0, 6)}...${cleanedWallet.slice(-4)}`)
        refreshMainMenu()
      } catch (error) {
        setWalletStatus(`Joined. Rewards sync failed: ${error instanceof Error ? error.message.slice(0, 60) : 'try again later'}`)
        refreshMainMenu()
      }
    })()
  }, [chosenUsername, sanitizeUsername, walletAddress, isChosenUsername, refreshMainMenu, connectToLobby, registerRewardUser, refreshClaimBalance])

  const connectWallet = React.useCallback(async () => {
    setWalletAddress('')
    setWalletProof(null)
    solanaWalletRef.current = null
    setWalletStatus('Connecting wallet...')
    refreshMainMenu()
    const anyWindow = window as any
    try {
      const solanaProviders = [anyWindow.phantom?.solana, anyWindow.solflare, anyWindow.jupiter?.solana, anyWindow.jupiter, anyWindow.solana, ...(Array.isArray(anyWindow.solana?.providers) ? anyWindow.solana.providers : [])]
      const solana = solanaProviders.find((provider: any) => provider?.connect != null && provider?.signMessage != null)
      if (solana != null) {
        const response = await solana.connect()
        const address = response?.publicKey?.toString?.()
        if (address != null && address.length > 0) {
          const message = buildWalletMessage(address)
          const encodedMessage = new TextEncoder().encode(message)
          setWalletStatus('Sign the Karat FPS login message in your wallet...')
          const signed = await solana.signMessage(encodedMessage, 'utf8')
          const signature = signed?.signature
          if (signature == null) {
            throw new Error('wallet did not return a signature')
          }
          solanaWalletRef.current = solana
          setWalletAddress(address)
          setWalletProof({
            walletType: 'solana',
            message,
            signature: bytesToBase64(signature instanceof Uint8Array ? signature : new Uint8Array(signature)),
            signatureEncoding: 'base64',
          })
          setWalletStatus(`Wallet: ${address.slice(0, 4)}...${address.slice(-4)} signed`)
          await refreshClaimBalance(address).catch(() => null)
          refreshMainMenu()
          return
        }
      }
      if (anyWindow.ethereum?.request != null) {
        const accounts = await anyWindow.ethereum.request({ method: 'eth_requestAccounts' })
        const address = accounts?.[0]
        if (address != null && address.length > 0) {
          const message = buildWalletMessage(address)
          setWalletStatus('Sign the Karat FPS login message in your wallet...')
          const signature = await anyWindow.ethereum.request({
            method: 'personal_sign',
            params: [message, address],
          })
          if (typeof signature !== 'string' || signature.length === 0) {
            throw new Error('wallet did not return a signature')
          }
          solanaWalletRef.current = null
          setWalletAddress(address)
          setWalletProof({
            walletType: 'evm',
            message,
            signature,
            signatureEncoding: 'hex',
          })
          setWalletStatus(`Wallet: ${address.slice(0, 6)}...${address.slice(-4)} signed`)
          refreshMainMenu()
          return
        }
      }
      setWalletStatus('No wallet found. Open Phantom, Solflare, Backpack, Jupiter, or MetaMask.')
      refreshMainMenu()
    } catch (error) {
      setWalletAddress('')
      setWalletProof(null)
      solanaWalletRef.current = null
      setWalletStatus('Wallet signature failed. Try again or spectate.')
      refreshMainMenu()
    }
  }, [buildWalletMessage, bytesToBase64, refreshClaimBalance, refreshMainMenu])

  const claimTokens = React.useCallback(async () => {
    const wallet = walletAddress.trim()
    const emitStatus = (message: string) => {
      setClaimStatus(message)
      setWalletStatus(message)
      refreshMainMenu()
      window.setTimeout(() => {
        try {
          BananaBread?.execute?.(`echo "${message.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`)
        } catch {
          // In-menu status above is the source of truth.
        }
      }, 0)
    }

    if (!/^0x[a-fA-F0-9]{40}$/.test(wallet)) {
      emitStatus('Add your 0x Robinhood wallet first.')
      return
    }

    emitStatus('Requesting GLD payout...')
    try {
      const response = await fetch(`${KARAT_API_URL}/claims`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ wallet }),
      })
      const result = await readApiJson(response)
      if (!response.ok || result?.ok === false) {
        throw new Error(result?.error || 'payout request failed')
      }

      const kills = Number(result?.killsClaimed ?? 0)
      const tokens = Number(result?.tokensClaimed ?? 0)
      const message = result?.claimed
        ? `Payout requested: ${kills} karats = ${tokens} GLD.`
        : String(result?.message || 'No unclaimed kills yet.')
      emitStatus(message)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Payout request failed.'
      emitStatus(message.slice(0, 120))
    }
  }, [walletAddress, readApiJson, refreshMainMenu])

  const pasteWalletFromClipboard = React.useCallback(async () => {
    try {
      if (navigator.clipboard?.readText == null) {
        setWalletStatus('Clipboard paste is blocked. Use Ctrl+V or long-press paste.')
        refreshMainMenu()
        return
      }

      const text = (await navigator.clipboard.readText()).trim()
      const match = text.match(/0x[a-fA-F0-9]{40}/)
      if (!match) {
        setWalletStatus('Clipboard does not contain a 0x wallet.')
        refreshMainMenu()
        return
      }

      const wallet = match[0]
      setWalletAddress(wallet)
      setWalletProof(null)
      setWalletStatus(`Wallet pasted: ${wallet.slice(0, 6)}...${wallet.slice(-4)}`)
      refreshMainMenu()
    } catch (error) {
      setWalletStatus('Clipboard blocked. Tap the wallet field then paste.')
      refreshMainMenu()
    }
  }, [refreshMainMenu])

  React.useEffect(() => {
    ;(Module as any).fragfi = {
      ...((Module as any).fragfi ?? {}),
      setWallet: (wallet: string) => {
        const cleaned = wallet.trim()
        setWalletAddress(cleaned)
        setWalletProof(null)
        setWalletStatus(cleaned.length > 0 ? `Wallet: ${cleaned.slice(0, 6)}...${cleaned.slice(-4)}` : 'Wallet optional. Add one only if you want GLD rewards.')
      },
      connectWallet,
      pasteWalletFromClipboard,
      claimTokens,
      joinGame,
      joinGameWithUsername: joinGame,
      setUsername: (username: string) => setChosenUsername(sanitizeUsername(username)),
      spectate: startAsGuest,
      status: () => walletStatus,
      claimStatus: () => claimStatus,
      wallet: () => walletAddress,
      privateLink: () => privateLink,
      privateStatus: () => privateStatus,
    }
  }, [connectWallet, pasteWalletFromClipboard, claimTokens, joinGame, startAsGuest, walletAddress, walletStatus, claimStatus, privateLink, privateStatus, sanitizeUsername])

  React.useEffect(() => {
    Module.gameState = {
      playerState: (
        health: number,
        maxHealth: number,
        gunSelect: number,
        shotgunAmmo: number,
        chainggunAmmo: number,
        rocketAmmo: number,
        rifleAmmo: number,
        grenadeAmmo: number,
        pistolAmmo: number
      ) => {
        setPlayerState({
          health,
          maxHealth,
          weapon: gunSelect,
          ammo: {
            [WeaponType.Saw]: 1,
            [WeaponType.Shotgun]: shotgunAmmo,
            [WeaponType.Chaingun]: chainggunAmmo,
            [WeaponType.Rocket]: rocketAmmo,
            [WeaponType.Rifle]: rifleAmmo,
            [WeaponType.Grenade]: grenadeAmmo,
            [WeaponType.Pistol]: pistolAmmo,
          },
        })
      },
    }
  }, [])

  React.useEffect(() => {
    ;(async () => {
      // Wait for the Emscripten runtime/heap to exist before mounting files.
      // Do not wait for WASM_PROMISE here: that resolves from Module.onGameReady(),
      // which only runs after Module.run(). Waiting on it before Module.run() deadlocks
      // the loader at the initial 18% screen.
      await new Promise<void>((resolve) => {
        const waitForRuntime = () => {
          if (Module.calledRun && Module.HEAPU8 != null && Module.FS_createDataFile != null) {
            resolve()
            return
          }
          window.setTimeout(waitForRuntime, 25)
        }
        waitForRuntime()
      })

      // Not a mod but I'm lazy
      await loadAsset(LoadRequestType.Mod, 'environment')

      // Load the basic required data for the game
      await loadAsset(LoadRequestType.Mod, 'base')
      await loadAsset(LoadRequestType.Mod, 'fps')

      const {
        location: { search: params },
      } = window

      const parsedParams = new URLSearchParams(params)
      let mods: string[] = getInstalledMods()
      if (parsedParams.has('safemode')) {
        mods = []
      }
      if (parsedParams.has('mods')) {
        const urlMods = parsedParams.get('mods')
        if (urlMods != null) {
          mods = urlMods.split(',')
        }
      }

      if (mods.length > 0) {
        await Promise.all(
          R.map(async (id: string) => {
            const mod = getMod(id)
            if (mod == null) {
              failedMods = [...failedMods, id]
              return
            }

            const { name } = mod

            try {
              const layer = await loadAsset(LoadRequestType.Mod, id)
              if (layer == null) {
                failedMods = [...failedMods, name]
                return
              }
              loadedMods = [...loadedMods, name]
            } catch (e) {
              failedMods = [...failedMods, name]
            }
          }, mods)
        )
      }

      shouldRunNow = true
      calledRun = false
      Module.calledRun = false
      Module.run()
    })()

    Module.socket = (addr, port) => {
      const { protocol } = window.location
      const prefix = `${protocol === 'https:' ? 'wss://' : 'ws:/'}${CONFIG.proxy}`

      return new WebSocket(
        addr === 'fragfi' ? prefix : `${prefix}u/${addr}:${port}`,
        ['binary']
      )
    }

    Module.print = (text) => {
      if (text === 'init: sdl') {
        setState({
          type: GameStateType.Running,
        })
      }

      // Do not auto-assign a random name. Karat FPS play requires the user to choose one in the menu.
      if (text === 'setting name to: unnamed') {
        return
      }

      const legacyCreditMessage = /\bby\s+(Nieb|eihrul|Dacker|blahville|viruz|Wouter|Aardappel)\b/i.test(text)
      if (legacyCreditMessage) {
        return
      }

      if (text.startsWith('main loop blocker')) {
        return
      }

      console.log(text)
    }
  }, [])

  const setResolution = React.useCallback(
    (width: Maybe<number>, height: Maybe<number>) => {
      if (BROWSER.isMobile) {
        const ratio = window.devicePixelRatio || 1
        const {
          documentElement: { clientWidth, clientHeight },
        } = document
        const width = clientWidth * ratio
        const height = clientHeight * ratio
        const { canvas } = Module
        if (canvas == null) return

        if (Module.running) {
          if (BananaBread == null || BananaBread.execute == null) return
          BananaBread.execute(`screenres ${width * 2} ${height * 2}`)
        }

        canvas.style.setProperty('width', clientWidth + 'px', 'important')
        canvas.style.setProperty('height', clientHeight + 'px', 'important')
        canvas.width = width * 2
        canvas.height = height * 2
        return
      }

      if (width == null || height == null) return
      Module.desiredWidth = width
      Module.desiredHeight = height
      if (Module.setCanvasSize == null) return
      Module.setCanvasSize(width, height)
      if (BananaBread == null || BananaBread.execute == null) return
      BananaBread.execute(`screenres ${width} ${height}`)
    },
    []
  )

  React.useEffect(() => {
    let frame: Maybe<number> = null

    const measure = () => {
      frame = null
      const element = containerRef.current
      if (element == null) return
      const rect = element.getBoundingClientRect()
      const nextWidth = Math.round(rect.width)
      const nextHeight = Math.round(rect.height)
      setContainerSize((current) => {
        if (current.width === nextWidth && current.height === nextHeight) {
          return current
        }
        return { width: nextWidth, height: nextHeight }
      })
    }

    const scheduleMeasure = () => {
      if (frame != null) return
      frame = window.requestAnimationFrame(measure)
    }

    scheduleMeasure()
    window.addEventListener('resize', scheduleMeasure)
    window.visualViewport?.addEventListener('resize', scheduleMeasure)
    window.visualViewport?.addEventListener('scroll', scheduleMeasure)

    return () => {
      if (frame != null) window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', scheduleMeasure)
      window.visualViewport?.removeEventListener('resize', scheduleMeasure)
      window.visualViewport?.removeEventListener('scroll', scheduleMeasure)
    }
  }, [])

  React.useEffect(() => {
    setResolution(containerSize.width, containerSize.height)
  }, [containerSize.width, containerSize.height, setResolution])

  React.useEffect(() => {
    if (state.type !== GameStateType.Ready) return

    const safeWalletStatus = walletStatus.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
    const safeClaimStatus = claimStatus.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
    const safeEarningsLines = earningsLeaderboard.length > 0
      ? earningsLeaderboard.slice(0, 5).map((player, index) => {
        const name = String(player.username || 'player').replace(/\\/g, '\\\\').replace(/"/g, '\\"').slice(0, 18)
        const earned = Number(player.gold_earned || 0).toFixed(5)
        return `        guitext "${index + 1}. ${name} - ${earned} GLD" 0`
      }).join('\n')
      : '        guitext "Loading all-time GLD leaderboard..." 0'
    const safePrivateStatus = privateStatus.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
    const safePrivateLink = privateLink.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
    const safeWalletLine = (walletAddress.length > 0
      ? walletStatus
      : safeWalletStatus
    ).replace(/\\/g, '\\\\').replace(/"/g, '\\"')
    const privateServerMenu = walletAddress.length > 0
      ? `
        guitext "Private Server" 0
        guitext "${safePrivateStatus}" 0
        guibutton "[ CREATE PRIVATE ]" [js "Module.fragfi.createPrivateLink(); ''"] "server"
        guibutton "[ COPY PRIVATE LINK ]" [js "Module.fragfi.copyPrivateLink(); ''"] "action"
        if (= (strlen "${safePrivateLink}") 0) [] [
            guitext "${safePrivateLink}" 0
        ]`
      : `
        guitext "Private Server" 0
        guitext "Enter wallet first." 0`

    const menu = `
    newgui discord [
        guibutton "copy authkey command.." [js "Module.discord.copyKey()"]
        //guibutton "regenerate auth key.." [js "Module.discord.regenKey()"]
        guibutton "log out.." [js "Module.discord.logout()"]
    ]

    newgui content [
        guibutton "mods.."  "showgui mods"
        guibutton "put mods in url.."  [js "Module.assets.modsToURL()"]
        guibutton "reload page.."  [js "window.location.reload()"]
    ]

    injectedmenu = [
        guitext "Karat FPS" 0
        guibar
        guitext "Start" 0
        newname = (getname)
        guitext "Name" 0
        guifield newname 16 [name $newname; js (concatword "Module.fragfi.setUsername('" $newname "'); ''")]
        walletaddr = "${walletAddress}"
        guitext "Wallet (optional rewards)" 0
        guifield walletaddr 64 [js (concatword "Module.fragfi.setWallet('" $walletaddr "'); ''")]
        guibutton "[ PASTE WALLET ]" [js "Module.fragfi.pasteWalletFromClipboard(); ''"] "action"
        guibutton (concat "Skin: " (playermodelname)) [chooseplayermodel] "action"
        guibutton "[ PLAY FFA ]" [name $newname; js (concatword "Module.fragfi.joinGameWithUsername('" $newname "'); ''")] "action"
        guibutton "[ SPECTATE ]" [js "Module.fragfi.spectate(); ''"] "spectator"
        if (isconnected) [
            guibar
            guitext "In game" 0
            guibutton "[ RESUME ]" [cleargui 1] "action"
            guibutton "[ DISCONNECT ]" "disconnect" "exit"
        ] []
        guitab "extra"
        ${privateServerMenu}
        guibar
        guitext "Rewards" 0
        guitext "${safeClaimStatus}" 0
        guitext "Request payout when ready: 0.00025 GLD / karat" 0
        guibutton "[ REQUEST PAYOUT ]" [js "Module.fragfi.claimTokens(); ''"] "coins"
        guibar
        guitext "All-time GLD Earned" 0
${safeEarningsLines}
        guibar
        guibutton "[ X / TWITTER ]" [js "window.open('https://x.com/karatpons', '_blank'); ''"] "action"
        guibutton "[ PONS LAUNCH ]" [js "window.open('https://www.ponsfamily.com/launchpad/0xbfe5484cb8e9e40b5df0f9c039dadd2ee17cd086', '_blank'); ''"] "action"
        guibutton "[ OPTIONS ]" "showgui options" "action"
    ]
    `
    BananaBread.execute(menu)
  }, [authState, state, walletAddress, walletProof, walletStatus, claimStatus, earningsLeaderboard, privateLink, privateStatus])

  React.useEffect(() => {
    // All commands in flight
    let commands: CommandRequest[] = []

    const [serverURL] = CONFIG.servers

    const { protocol, host } = window.location
    const ws = new WebSocket(
      `${protocol === 'https:' ? 'wss://' : 'ws:/'}${serverURL}`
    )
    ws.binaryType = 'arraybuffer'

    ws.onopen = () => {
      const { current: queue } = wsQueue
      if (queue == null) return
      for (const message of queue) {
        send(message)
      }
    }

    wsRef.current = ws

    const runCommand = async (command: string) => {
      const generate = (): number => Math.floor(Math.random() * 2048)

      let id: number = generate()

      // We don't want collisions and can't use a Symbol
      while (R.find((v) => v.id === id, commands) != null) {
        id = generate()
      }

      const promiseSet = breakPromise<string>()

      commands = [
        ...commands,
        {
          id,
          promiseSet,
        },
      ]

      const message: CommandMessage = {
        Op: MessageType.Command,
        Command: command,
        Id: id,
      }

      send(CBOR.encode(message))

      return promiseSet.promise
    }

    const copyText = async (text: string) => {
      try {
        await navigator.clipboard.writeText(text)
        return true
      } catch (error) {
        console.warn('failed to copy private link', error)
        return false
      }
    }

    ;(Module as any).fragfi = {
      ...((Module as any).fragfi ?? {}),
      createPrivateLink: () => {
        const wallet = walletAuthRef.current.address.trim()
        if (!EVM_WALLET_REGEX.test(wallet)) {
          setPrivateStatus('Enter 0x wallet first.')
          refreshMainMenu()
          return
        }
        send(
          CBOR.encode({
            Op: MessageType.WalletLogin,
            Wallet: wallet,
            WalletType: 'typed-evm',
            SpectatorOnly: false,
          })
        )
        setPrivateStatus('Creating private server...')
        ;(async () => {
          try {
            await runCommand('creategame ffa')
            setPrivateLink(window.location.href)
            setPrivateStatus('Private link ready. Send this URL to friends.')
          } catch (error) {
            const message = error instanceof Error ? error.message : String(error)
            setPrivateStatus(`Private server failed: ${message}`)
          }
        })()
      },
      copyPrivateLink: () => {
        const link = privateLink.length > 0 ? privateLink : (window.location.hash.startsWith('#/server/') ? window.location.href : '')
        if (link.length === 0) {
          setPrivateStatus('Create a private link first.')
          return
        }
        ;(async () => {
          const copied = await copyText(link)
          setPrivateStatus(copied ? 'Private link copied. Send it to friends.' : 'Copy failed. Use the URL bar link.')
        })()
      },
    }

    const injectServers = (servers: any) => {
      R.map((server) => {
        const { Host, Port, Info, Length } = server

        // Get data byte size, allocate memory on Emscripten heap, and get pointer
        const pointer = Module._malloc(Length)

        // Copy data to Emscripten heap (directly accessed from Module.HEAPU8)
        const dataHeap = new Uint8Array(Module.HEAPU8.buffer, pointer, Length)
        dataHeap.set(new Uint8Array(Info.buffer, Info.byteOffset, Length))

        // Call function and get result
        BananaBread.injectServer(Host, Port, pointer, Length)

        // Free memory
        Module._free(pointer)
      }, servers)
      BananaBread.execute('sortservers')
    }

    let serverEvents: SocketMessage[] = []
    let queuedEvents: SocketMessage[] = []
    let loadingWorld = false

    Module.running = false
    Module.postLoadWorld = function () {
      loadingWorld = false
      serverEvents = [...serverEvents, ...queuedEvents]
    }
    Module.addRunDependency = (file) => {
      console.log(`add ${file}`)
    }

    Module.interop = (command: string): number => {
      try {
        const result = window.eval(command)
        if (typeof result !== 'string' || result.length === 0) {
          return 0
        }
        const numBytes = lengthBytesUTF8(result) + 1
        const onHeap = Module._malloc(numBytes)
        stringToUTF8(result, onHeap, numBytes)
        // Sauer frees this on its own when it's done
        return onHeap
      } catch (e) {
        console.error(`failed to eval '${command}'`, e)
        return 0
      }
    }

    let remoteConnected: boolean = false

    let cachedServers: Maybe<any> = null
    Module.onGameReady = () => {
      onReadyAssets()
      const ensureFsPath = (parent: string, path: string) => {
        try {
          Module.FS_createPath(parent, path, true, true)
        } catch (e) {
          // Emscripten throws ErrnoError when the path already exists. onGameReady
          // can run more than once across reload/reconnect/map paths, so these
          // setup directories must be idempotent.
          try {
            const fullPath = parent === '/' ? `/${path}` : `${parent}/${path}`
            if (FS.analyzePath(fullPath).exists) return
          } catch (_ignored) {}
          throw e
        }
      }

      ensureFsPath(`/`, 'packages')
      ensureFsPath(`/packages`, 'base')
      ensureFsPath(`/packages`, 'prefab')
      ensureFsPath(`/`, 'demo')

      if (BROWSER.isFirefox || BROWSER.isSafari) {
        BananaBread.execute('skipparticles 1')
        BananaBread.execute('glare 0')
      } else {
        BananaBread.execute('skipparticles 0')
      }

      if (!BROWSER.isMobile) {
        BananaBread.execute(`
              fullscreendesktop 1
        `)
      }

      if (BROWSER.isMobile) {
        BananaBread.execute(`
              // mobile screens are really dark
              lazyshader 0 "mobilegamma" (fsvs) (fsps [gl_FragColor.rgb = pow(color.rgb, vec3(1.0/1.8));])
              setpostfx mobilegamma
              forceplayermodels
              playermodel 1
              gui2d 1
              skyboxglare 0
              fullscreendesktop 0
              skipskybox 1
        `)
      }

      Module.running = true
      setResolution(null, null)
      setState({
        type: GameStateType.Ready,
      })

      if (cachedServers != null) {
        injectServers(cachedServers)
      }

      if (loadedMods.length > 0) {
        log.success(
          `loaded mods: ${R.join(
            ', ',
            loadedMods
          )}. if you experience issues, add ?safemode to the URL.`
        )
      }
      if (failedMods.length > 0) {
        log.error(`failed to load mods: ${R.join(', ', failedMods)}`)
      }

      const {
        location: { search: params, hash },
      } = window

      const openMainMenu = () => {
        ;[0, 250, 1000].forEach((delay) => {
          setTimeout(() => BananaBread.execute('showgui main'), delay)
        })
      }

      const serverDestination = SERVER_URL_REGEX.exec(hash)
      const mapDestination = MAP_URL_REGEX.exec(hash)
      if (serverDestination != null) {
        const [, hostname, port] = serverDestination
        if (port == null) {
          BananaBread.execute(`join ${hostname}`)
        } else {
          BananaBread.execute(`connect ${hostname} ${port}`)
        }
      } else if (mapDestination != null) {
        const [, mapId] = mapDestination
        BananaBread.execute(`map ${mapId}`)
        openMainMenu()
      } else if (hash.startsWith('/demo/')) {
        // First check the fragment for a URL
        if (hash.length !== 0) {
          const url = hash.slice(1)
          remoteConnected = true
          playDemoURL(url, 'from-url')
        } else {
          const demoDestination = DEMO_URL_REGEX.exec(hash)
          if (demoDestination != null) {
            const [, demoId] = demoDestination
            const [serverURL] = CONFIG.servers
            const { protocol } = window.location
            const demoURL = `${protocol}//${serverURL}api/demo/${demoId}`
            remoteConnected = true
            playDemoURL(demoURL, demoId)
          } else {
            pushURLState('#')
          }
        }
      } else {
        // It should not be anything else
        pushURLState('#')
        openMainMenu()
      }

      const parsedParams = new URLSearchParams(params)

      initializeDiscord(
        parsedParams.has('code') ? parsedParams.get('code') : null
      )

      if (parsedParams.has('cmd')) {
        const cmd = parsedParams.get('cmd')
        if (cmd == null) return
        setTimeout(() => BananaBread.execute(cmd), 0)
      }

      if (parsedParams.has('safemode')) {
        log.info(
          'you are in safe mode. no mods were loaded, but you can disable them in the mod menu.'
        )
      }
    }

    const updateServerURL = (name: string, port: number) => {
      // Karat FPS server
      if (port === 0) {
        pushURLState(`#/server/${name}`)
        return
      }

      pushURLState(`#/server/${name}/${port}`)
    }

    Module.onConnect = () => {}
    Module.onDisconnect = () => {
      remoteConnected = false
      clearURLState()
    }

    Module.loadedMap = (name: string) => {
      if (remoteConnected) return
      pushURLState(`#/map/${name}`)
    }

    let lastPointer: number = 0
    let lastPointerLength: number = 0

    // Only allocate memory if we really need to
    const malloc = (size: number) => {
      // reduce, reuse, recycle
      if (size <= lastPointerLength) {
        return lastPointer
      }

      if (lastPointer !== 0) {
        Module._free(lastPointer)
      }

      const pointer = Module._malloc(size)
      lastPointer = pointer
      lastPointerLength = size
      return pointer
    }

    Module.onLocalDisconnect = () => {
      clearURLState()
    }

    Module.cluster = {
      createGame: (preset: string, mode: string) => {
        log.info('creating private game...')
        ;(async () => {
          try {
            console.log(`creategame ${preset} ${mode}`)
            const result = await runCommand(`creategame ${preset} ${mode}`)
            log.success('created game!')
          } catch (e) {
            log.error(`failed to create private game: ${e}`)
          }
        })()
      },
      connect: (name: string, password: string) => {
        const Target = name.length === 0 ? 'lobby' : name
        send(
          CBOR.encode({
            Op: MessageType.Connect,
            Target,
          })
        )
      },
      send: (channel: number, dataPtr: number, dataLength: number) => {
        const packet = new Uint8Array(dataLength)
        packet.set(new Uint8Array(Module.HEAPU8.buffer, dataPtr, dataLength))
        if (DEBUG) {
          const p = cube.newPacket(packet)
          const msgType = cube.getInt(p)
          if (msgType != null) {
            console.log(
              `%c client -> server type=${CubeMessageType[msgType]}`,
              'background-color: blue; color: white'
            )
          }
        }
        send(
          CBOR.encode({
            Op: MessageType.Packet,
            Channel: channel,
            Data: packet,
            Length: dataLength,
          })
        )
      },
      receive: (dataPtr: number, dataLengthPtr: number) => {
        const view = new DataView(Module.HEAPU8.buffer)

        const message = serverEvents.shift()
        if (message == null) {
          return 0
        }

        if (message.Op === MessageType.ServerConnected) {
          // Layout:
          // 2: Event
          const frameLength = 2
          const pointer = malloc(frameLength)
          view.setUint16(pointer, ENetEventType.Connect, true)
          return pointer
        }

        if (message.Op === MessageType.ServerDisconnected) {
          const { Reason } = message

          // Layout:
          // 2: Event
          // 2: Reason
          const frameLength = 2 + 2
          const pointer = malloc(frameLength)
          view.setUint16(pointer, ENetEventType.Disconnect, true)
          view.setUint16(pointer + 2, Reason, true)
          return pointer
        }

        const { Channel, Data, Length } = message

        // Layout:
        // 2: Event
        // 2: Channel
        // 4: Length
        // N: Data
        const frameLength = 2 + 2 + 4 + Length
        const pointer = malloc(frameLength)

        // sourEvent
        view.setUint16(pointer, ENetEventType.Receive, true)
        // sourChannel
        view.setUint16(pointer + 2, Channel, true)
        // dataLength
        view.setUint32(pointer + 4, Length, true)

        // Copy in from data
        const dataHeap = new Uint8Array(
          Module.HEAPU8.buffer,
          pointer + 8,
          Length
        )
        dataHeap.set(new Uint8Array(Data.buffer, Data.byteOffset, Length))

        return pointer
      },
      disconnect: () => {
        clearURLState()
        send(
          CBOR.encode({
            Op: MessageType.Disconnect,
          })
        )
      },
    }

    ws.onmessage = (evt) => {
      const serverMessage: ServerMessage = CBOR.decode(evt.data)

      if (serverMessage.Op === MessageType.Info) {
        const { Cluster, Master } = serverMessage

        if (
          BananaBread == null ||
          BananaBread.execute == null ||
          BananaBread.injectServer == null
        ) {
          cachedServers = Master
          return
        }

        injectServers(Master)
        return
      }

      if (serverMessage.Op === MessageType.ServerConnected) {
        remoteConnected = true
        const { Server, Internal, Owned } = serverMessage
        if (Internal) {
          clearURLState()
        } else {
          updateServerURL(Server, 0)
          if (Owned) {
            const link = `${window.location.origin}${window.location.pathname}${window.location.search}#/server/${Server}`
            setPrivateLink(link)
            setPrivateStatus('Private link ready. Send this URL to friends.')
            copyText(link).then((copied) => {
              if (copied) {
                setPrivateStatus('Private link copied. Send it to friends.')
              }
            })
          }
        }
        // intentional fallthrough
      }

      if (serverMessage.Op === MessageType.ServerResponse) {
        const { Id, Response, Success } = serverMessage
        const request = R.find(({ id: otherId }) => Id === otherId, commands)
        if (request == null) return

        const {
          promiseSet: { resolve, reject },
        } = request

        if (Success) {
          resolve(Response)
        } else {
          reject(new Error(Response))
        }

        commands = R.filter(({ id: otherId }) => Id !== otherId, commands)
        return
      }

      if (serverMessage.Op === MessageType.Packet) {
        const packet = cube.newPacket(serverMessage.Data)
        const msgType = cube.getInt(packet)

        if (msgType === CubeMessageType.N_MAPCHANGE) {
          loadingWorld = true
          serverEvents.push(serverMessage)
          return
        }

        if (msgType != null) {
          if (DEBUG) {
            console.log(
              `%c server -> client type=${CubeMessageType[msgType]}`,
              'background-color: green; color: white'
            )
          }
          if (loadingWorld && !DELAY_AFTER_LOAD.includes(msgType)) {
            serverEvents.push(serverMessage)
            return
          }
        }
      }

      if (serverMessage.Op === MessageType.AuthSucceeded) {
        receiveAuthMessage(serverMessage)
        return
      }

      if (serverMessage.Op === MessageType.Chat) {
        const legacyCreditMessage = /\bby\s+(Nieb|eihrul|Dacker|blahville|viruz|Wouter|Aardappel)\b/i.test(serverMessage.Message)
        if (!legacyCreditMessage) {
          log.chat(serverMessage.Message)
        }
        if (
          serverMessage.Message.includes('Wallet signature failed') ||
          serverMessage.Message.includes('Sign wallet')
        ) {
          setWalletStatus(serverMessage.Message)
        }
        return
      }

      if (serverMessage.Op === MessageType.AuthFailed) {
        receiveAuthMessage(serverMessage)
        return
      }

      if (loadingWorld) {
        queuedEvents.push(serverMessage)
      } else {
        serverEvents.push(serverMessage)
      }
    }
  }, [])

  React.useLayoutEffect(() => {
    const { canvas } = Module
    if (canvas == null) return

    // As a default initial behavior, pop up an alert when webgl context is lost. To make your
    // application robust, you may want to override this behavior before shipping!
    // See http://www.khronos.org/registry/webgl/specs/latest/1.0/#5.15.2
    canvas.addEventListener(
      'webglcontextlost',
      function (e) {
        alert('WebGL context lost. You will need to reload the page.')
        e.preventDefault()
      },
      false
    )

    //canvas.addEventListener('click', function () {
    //canvas.requestPointerLock()
    //})

    return
  }, [])

  return (
    <OuterContainer ref={containerRef}>
      <GameContainer>
        <canvas
          className="game"
          style={{ opacity: state.type !== GameStateType.Ready ? 0 : 1 }}
          id="canvas"
          ref={(canvas) => {
            if (canvas != null) {
              // This is a bug in mobile Safari where Reader holds on to canvas refs
              // https://gist.github.com/eugeneware/bb69a5bce8c8c48178845429435458e2#file-safari_reader-js-L1937
              // @ts-ignore
              canvas._evaluatedForTextContent = true
              // @ts-ignore
              canvas._cachedElementBoundingRect = {}
            }
            Module.canvas = canvas
          }}
          onContextMenu={(event) => event.preventDefault()}
        ></canvas>
        {BROWSER.isMobile && (
          <MobileControls
            playerState={playerState}
            isRunning={state.type === GameStateType.Ready}
          />
        )}
      </GameContainer>
      {state.type !== GameStateType.Ready && (
        <LoadingContainer>
          <Box w="100%" h="100%">
            <StatusOverlay state={state} />
          </Box>
        </LoadingContainer>
      )}
      <FileDropper>
        <DropTarget>
          <Flex align="center" justify="center">
            <VStack paddingTop="20%">
              <Heading>Drop a demo file here...</Heading>
            </VStack>
          </Flex>
        </DropTarget>
      </FileDropper>
    </OuterContainer>
  )
}

ReactDOM.render(
  <ChakraProvider theme={theme}>
    <App />
  </ChakraProvider>,
  document.getElementById('root')
)
